import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import TrendingUp from '@material-ui/icons/TrendingUp';
import WorkOutline from '@material-ui/icons/WorkOutline';
import AccountBalanceWallet from '@material-ui/icons/AccountBalanceWallet';
import PieChartIcon from '@material-ui/icons/PieChart';
import BarChartIcon from '@material-ui/icons/BarChart';
import Chart from 'react-apexcharts';

import { dashbordStatsRequest } from '../../actions';
import Loader from '../../components/Loader/Loader';

function formatInr(n) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);
}

function isoToDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

const ACCENT = {
  primary: '#3949AB',
  teal: '#00897B',
  coral: '#E64A19',
  violet: '#7E57C2',
  amber: '#FFA726',
  slate: '#546E7A',
};

const CHART_COLORS = [
  '#3949AB',
  '#00897B',
  '#E64A19',
  '#7E57C2',
  '#42A5F5',
  '#FF7043',
  '#26A69A',
  '#AB47BC',
  '#78909C',
];

const useStyles = makeStyles((theme) => ({
  root: {
    marginLeft: 130,
    marginTop: 56,
    padding: theme.spacing(0, 2, 4),
    maxWidth: 1400,
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(2),
      padding: theme.spacing(1),
    },
  },
  hero: {
    background: `linear-gradient(135deg, ${ACCENT.primary} 0%, #1A237E 55%, #0D1642 100%)`,
    color: theme.palette.common.white,
    borderRadius: 16,
    padding: theme.spacing(3, 3, 2.5),
    marginBottom: theme.spacing(3),
    boxShadow: '0 12px 40px rgba(26, 35, 126, 0.35)',
  },
  heroTitle: {
    fontWeight: 700,
    letterSpacing: '-0.02em',
    fontSize: '1.75rem',
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.35rem',
    },
  },
  heroSubtitle: {
    opacity: 0.92,
    marginTop: theme.spacing(0.75),
    fontSize: '0.95rem',
    maxWidth: 640,
    lineHeight: 1.5,
  },
  filterRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  filterBtn: {
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.55)',
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,0.5)',
      borderColor: 'rgba(255,255,255,0.25)',
    },
  },
  filterBtnActive: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.28)',
    },
  },
  dateField: {
    minWidth: 150,
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.75)' },
    '& .MuiOutlinedInput-root': {
      color: '#fff',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.45)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.75)' },
    },
    '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.85)' },
  },
  statCard: {
    borderRadius: 14,
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    position: 'relative',
  },
  statCardTint: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  statIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5),
  },
  statLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  statValue: {
    fontSize: '1.65rem',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.25),
  },
  chartPaper: {
    borderRadius: 14,
    padding: theme.spacing(2, 2, 1),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    height: '100%',
  },
  chartTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chartHint: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
}));

function StatCard({ classes, tint, iconBg, icon, label, value, sub }) {
  return (
    <Paper className={classes.statCard} elevation={0}>
      <div className={classes.statCardTint} style={{ background: tint }} />
      <Box p={2.5} pt={2}>
        <div className={classes.statIconWrap} style={{ background: iconBg }}>
          {icon}
        </div>
        <Typography className={classes.statLabel}>{label}</Typography>
        <Typography className={classes.statValue}>{value}</Typography>
        {sub ? (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

const Home = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const stats = useSelector((state) => state.Dashboard.dashboardStats);
  const { loader } = useSelector((state) => state.Loader);
  const [filterMode, setFilterMode] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    dispatch(dashbordStatsRequest({}));
  }, [dispatch]);

  const fullDashboard = stats && stats.dashboardScope === 'full';
  const ready = stats && stats.period;

  const jobsBarOptions = useMemo(() => {
    if (!ready) return {};
    const categories = stats.jobsByMonth.map((x) => x.month);
    return {
      chart: {
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: [ACCENT.primary],
      plotOptions: {
        bar: {
          borderRadius: 8,
          columnWidth: '55%',
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: { style: { colors: '#546E7A', fontSize: '11px' } },
      },
      yaxis: {
        labels: { style: { colors: '#546E7A' } },
        title: { text: 'Jobs' },
      },
      grid: { borderColor: '#ECEFF1', strokeDashArray: 4 },
      tooltip: { theme: 'light' },
    };
  }, [ready, stats]);

  const jobsBarSeries = useMemo(() => {
    if (!ready) return [];
    return [{ name: 'Jobs', data: stats.jobsByMonth.map((x) => x.count) }];
  }, [ready, stats]);

  const revenueAreaOptions = useMemo(() => {
    if (!ready || !fullDashboard) return {};
    const categories = stats.revenueByMonth.map((x) => x.month);
    return {
      chart: {
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: [ACCENT.teal],
      stroke: { curve: 'smooth', width: 3 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: { style: { colors: '#546E7A', fontSize: '11px' } },
      },
      yaxis: {
        labels: {
          style: { colors: '#546E7A' },
          formatter: (val) => formatInr(val),
        },
        title: { text: 'Revenue' },
      },
      grid: { borderColor: '#ECEFF1', strokeDashArray: 4 },
      tooltip: {
        y: {
          formatter: (val) => formatInr(val),
        },
      },
    };
  }, [ready, stats, fullDashboard]);

  const revenueAreaSeries = useMemo(() => {
    if (!ready || !fullDashboard) return [];
    return [{ name: 'Revenue', data: stats.revenueByMonth.map((x) => x.total) }];
  }, [ready, stats, fullDashboard]);

  const kpiBarOptions = useMemo(() => {
    if (!ready || !fullDashboard) return {};
    return {
      chart: {
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        toolbar: { show: false },
      },
      colors: [ACCENT.primary, ACCENT.coral, ACCENT.teal],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 10,
          columnWidth: '42%',
          distributed: true,
        },
      },
      legend: { show: false },
      dataLabels: {
        enabled: true,
        formatter: (val) => formatInr(val),
        style: { fontSize: '11px' },
      },
      xaxis: {
        categories: ['Revenue', 'Cost', 'Profit'],
        labels: { style: { colors: '#546E7A' } },
      },
      yaxis: {
        labels: {
          formatter: (val) => formatInr(val),
          style: { colors: '#546E7A' },
        },
      },
      grid: { borderColor: '#ECEFF1', strokeDashArray: 4 },
      tooltip: { y: { formatter: (val) => formatInr(val) } },
    };
  }, [ready, fullDashboard]);

  const kpiBarSeries = useMemo(() => {
    if (!ready || !fullDashboard) return [];
    return [
      {
        name: 'Amount',
        data: [stats.revenue, stats.cost, stats.profit],
      },
    ];
  }, [ready, stats, fullDashboard]);

  const destPieOptions = useMemo(() => {
    if (!ready || !fullDashboard) return {};
    return {
      chart: {
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        toolbar: { show: false },
      },
      labels: stats.jobsByDestinationCity.labels,
      colors: CHART_COLORS,
      legend: { position: 'bottom', fontSize: '12px' },
      dataLabels: { enabled: true },
      plotOptions: {
        pie: {
          donut: {
            size: '58%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Jobs',
                formatter: () => String(stats.totalJobs),
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} jobs`,
        },
      },
    };
  }, [ready, stats, fullDashboard]);

  const destPieSeries = useMemo(() => {
    if (!ready || !fullDashboard || !stats.jobsByDestinationCity.values.length) return [];
    return stats.jobsByDestinationCity.values;
  }, [ready, stats, fullDashboard]);

  const originPieOptions = useMemo(() => {
    if (!ready || !fullDashboard) return {};
    return {
      chart: {
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        toolbar: { show: false },
      },
      labels: stats.jobsByOriginCity.labels,
      colors: CHART_COLORS.slice().reverse(),
      legend: { position: 'bottom', fontSize: '12px' },
      dataLabels: { enabled: true },
      plotOptions: {
        pie: {
          expandOnClick: true,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} jobs`,
        },
      },
    };
  }, [ready, stats, fullDashboard]);

  const originPieSeries = useMemo(() => {
    if (!ready || !fullDashboard || !stats.jobsByOriginCity.values.length) return [];
    return stats.jobsByOriginCity.values;
  }, [ready, stats, fullDashboard]);

  const statusPieOptions = useMemo(() => {
    if (!ready) return {};
    return {
      chart: {
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        toolbar: { show: false },
      },
      labels: ['Ongoing', 'Completed', 'Other'],
      colors: [ACCENT.amber, ACCENT.teal, ACCENT.slate],
      legend: { position: 'bottom' },
      plotOptions: {
        pie: {
          donut: {
            size: '52%',
          },
        },
      },
    };
  }, [ready]);

  const statusPieSeries = useMemo(() => {
    if (!ready) return [];
    return [stats.statusBreakdown.ongoing, stats.statusBreakdown.completed, stats.statusBreakdown.other];
  }, [ready, stats]);

  const periodCaption = () => {
    if (!ready) return '';
    if (stats.period.mode === 'all') return 'All records combined';
    if (stats.period.mode === 'range') return 'Custom date range';
    if (stats.period.mode === 'fy') return `Financial year FY ${stats.period.label}`;
    return '';
  };

  const loadAllData = () => {
    setFilterMode('all');
    dispatch(dashbordStatsRequest({}));
  };

  const openCustomRange = () => {
    if (stats && stats.period) {
      setCustomFrom(isoToDateInput(stats.period.startISO));
      setCustomTo(isoToDateInput(stats.period.endISO));
    }
    setFilterMode('custom');
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    dispatch(dashbordStatsRequest({ from: customFrom, to: customTo }));
  };

  return (
    <div className={classes.root}>
      {loader ? <Loader /> : null}

      <Paper className={classes.hero} elevation={0}>
        <Typography className={classes.heroTitle}>Operations dashboard</Typography>
        <Typography className={classes.heroSubtitle}>
          {fullDashboard
            ? 'Jobs, revenue, and workload for your chosen period. Defaults to all data; use a custom date range to focus charts.'
            : 'Jobs and status for your chosen period. Defaults to all data; use a custom date range to focus charts. Revenue and location breakdowns are available to administrators only.'}
        </Typography>
        <Box className={classes.filterRow}>
          <Button
            color="inherit"
            size="small"
            variant={filterMode === 'all' ? 'contained' : 'outlined'}
            className={filterMode === 'all' ? classes.filterBtnActive : classes.filterBtn}
            onClick={loadAllData}
          >
            All data
          </Button>
          <Button
            color="inherit"
            size="small"
            variant={filterMode === 'custom' ? 'contained' : 'outlined'}
            className={filterMode === 'custom' ? classes.filterBtnActive : classes.filterBtn}
            onClick={openCustomRange}
          >
            Custom range
          </Button>
          {filterMode === 'custom' ? (
            <>
              <TextField
                className={classes.dateField}
                label="From"
                type="date"
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <TextField
                className={classes.dateField}
                label="To"
                type="date"
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
              <Button
                size="small"
                variant="contained"
                style={{ backgroundColor: '#fff', color: ACCENT.primary }}
                onClick={applyCustomRange}
                disabled={!customFrom || !customTo}
              >
                Apply
              </Button>
            </>
          ) : null}
        </Box>
      </Paper>

      {!ready ? (
        <Typography color="textSecondary">Loading dashboard…</Typography>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {periodCaption()}: <strong>{stats.period.startLabel}</strong> — <strong>{stats.period.endLabel}</strong>
          </Typography>

          <Grid container spacing={2}>
            {fullDashboard ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    classes={classes}
                    tint={ACCENT.primary}
                    iconBg="rgba(57, 73, 171, 0.12)"
                    icon={<AccountBalanceWallet htmlColor={ACCENT.primary} />}
                    label="Total revenue"
                    value={formatInr(stats.revenue)}
                    sub="Invoices in this period (or linked job date in period)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    classes={classes}
                    tint={ACCENT.teal}
                    iconBg="rgba(0, 137, 123, 0.12)"
                    icon={<TrendingUp htmlColor={ACCENT.teal} />}
                    label="Profit"
                    value={formatInr(stats.profit)}
                    sub={`Cost ${formatInr(stats.cost)}`}
                  />
                </Grid>
              </>
            ) : null}

            <Grid item xs={12} sm={fullDashboard ? 6 : 6} md={fullDashboard ? 3 : 6}>
              <StatCard
                classes={classes}
                tint={ACCENT.violet}
                iconBg="rgba(126, 87, 194, 0.12)"
                icon={<WorkOutline htmlColor={ACCENT.violet} />}
                label="Total jobs"
                value={String(stats.totalJobs)}
                sub={`${stats.statusBreakdown.completed} completed · ${stats.statusBreakdown.ongoing} ongoing`}
              />
            </Grid>
            <Grid item xs={12} sm={fullDashboard ? 6 : 6} md={fullDashboard ? 3 : 6}>
              <StatCard
                classes={classes}
                tint={ACCENT.coral}
                iconBg="rgba(230, 74, 25, 0.12)"
                icon={<BarChartIcon htmlColor={ACCENT.coral} />}
                label="Avg. jobs / month"
                value={
                  stats.avgJobsPerMonth != null
                    ? String(stats.avgJobsPerMonth)
                    : stats.totalJobs
                    ? (stats.totalJobs / Math.max(1, stats.jobsByMonth.length)).toLocaleString('en-IN', {
                        maximumFractionDigits: 1,
                      })
                    : '0'
                }
                sub="Average over months in this period"
              />
            </Grid>

            <Grid item xs={12} lg={fullDashboard ? 8 : 12}>
              <Paper className={classes.chartPaper} elevation={0}>
                <Typography className={classes.chartTitle}>
                  <BarChartIcon fontSize="small" htmlColor={ACCENT.primary} />
                  Jobs by month
                </Typography>
                <Typography className={classes.chartHint}>
                  Volume of jobs whose job date falls in each month of the selected period.
                </Typography>
                <Chart options={jobsBarOptions} series={jobsBarSeries} type="bar" height={320} />
              </Paper>
            </Grid>

            <Grid item xs={12} lg={fullDashboard ? 4 : 12}>
              <Paper className={classes.chartPaper} elevation={0}>
                <Typography className={classes.chartTitle}>
                  <PieChartIcon fontSize="small" htmlColor={ACCENT.teal} />
                  Job status
                </Typography>
                <Chart options={statusPieOptions} series={statusPieSeries} type="donut" height={320} />
              </Paper>
            </Grid>

            {fullDashboard ? (
              <>
                <Grid item xs={12}>
                  <Paper className={classes.chartPaper} elevation={0}>
                    <Typography className={classes.chartTitle}>
                      <TrendingUp fontSize="small" htmlColor={ACCENT.teal} />
                      Revenue trend by month
                    </Typography>
                    <Typography className={classes.chartHint}>Invoice totals attributed to each month in this period.</Typography>
                    <Chart options={revenueAreaOptions} series={revenueAreaSeries} type="area" height={300} />
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper className={classes.chartPaper} elevation={0}>
                    <Typography className={classes.chartTitle}>Revenue · Cost · Profit</Typography>
                    <Typography className={classes.chartHint}>Snapshot for the selected period.</Typography>
                    <Chart options={kpiBarOptions} series={kpiBarSeries} type="bar" height={300} />
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper className={classes.chartPaper} elevation={0}>
                    <Typography className={classes.chartTitle}>
                      <PieChartIcon fontSize="small" htmlColor={ACCENT.violet} />
                      Jobs by destination city
                    </Typography>
                    <Typography className={classes.chartHint}>Share of jobs by delivery city (top cities + Other).</Typography>
                    {destPieSeries.length ? (
                      <Chart options={destPieOptions} series={destPieSeries} type="donut" height={360} />
                    ) : (
                      <Typography color="textSecondary" style={{ padding: 24 }}>
                        No city data for jobs in this period.
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper className={classes.chartPaper} elevation={0}>
                    <Typography className={classes.chartTitle}>
                      <PieChartIcon fontSize="small" htmlColor={ACCENT.coral} />
                      Jobs by origin city
                    </Typography>
                    <Typography className={classes.chartHint}>Where moves start from (pickup city).</Typography>
                    {originPieSeries.length ? (
                      <Chart options={originPieOptions} series={originPieSeries} type="pie" height={360} />
                    ) : (
                      <Typography color="textSecondary" style={{ padding: 24 }}>
                        No city data for jobs in this period.
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </>
            ) : null}
          </Grid>
        </>
      )}
    </div>
  );
};

export default withRouter(Home);
